/**
 * Dismissing the navigation menus: a tap outside one, or the Escape key.
 *
 * Both menus on the bar are native `<details>` elements, which is what lets a
 * reader open them with no JavaScript at all — the defect phase 5 existed to
 * fix. A disclosure knows how to open and close itself, and this file adds
 * nothing to that: with the script blocked, the summary still toggles and every
 * destination on the site is still reachable.
 *
 * What a disclosure has no notion of is being dismissed. It closes when its own
 * summary is used and at no other time, so a reader who opens the tools menu and
 * then decides against it has to find the word "Tools" again to be rid of it,
 * and one who opens the whole menu on a phone has to find the hamburger. Every
 * other menu on the web closes when you tap away from it, and there is no way to
 * express "a pointer went down somewhere that is not this element" in CSS.
 *
 * **`pointerdown` rather than `click`, and that is not a preference.** iOS
 * Safari does not bubble a click from an element that is not itself interactive
 * up to the document, so a `click` listener here would work everywhere except
 * the phones this is most needed on. `pointerdown` has no such rule, and it also
 * dismisses on the press rather than on the release, which is what a reader
 * expects from a menu.
 *
 * Nested menus are handled by the containment test rather than by knowing the
 * shape of the bar: on a narrow screen the tools disclosure sits inside the
 * navigation one, so a tap on a tool is inside both and closes neither.
 */

/** The disclosures on the bar, in the order a document holds them. */
const menuSelector = ".cb-nav-disclosure, .cb-tools-menu";

/**
 * Wires both menus so a reader can get rid of one.
 *
 * Listeners on the document rather than one per menu, because what is being
 * listened for is everything that is *not* a menu, and that is the document's
 * business. Two listeners for a whole bar, whatever it grows.
 */
export function startNavMenus(document: Document): void {
  const menus = (): HTMLDetailsElement[] => [
    ...document.querySelectorAll<HTMLDetailsElement>(menuSelector),
  ];

  if (menus().length === 0) {
    return;
  }

  document.addEventListener("pointerdown", (event) => {
    closeMenusAwayFrom(menus(), event.target);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeInnermostMenu(menus());
    }
  });
}

/**
 * Closes every open menu that the given target is not inside.
 *
 * The summary is inside its own menu, so a press on it closes nothing here and
 * the browser's own toggle does the work. That is what keeps this an addition
 * to the native behaviour rather than a second implementation of it racing the
 * first.
 */
export function closeMenusAwayFrom(
  menus: readonly HTMLDetailsElement[],
  target: EventTarget | null,
): void {
  const node = target instanceof Node ? target : undefined;

  for (const menu of menus) {
    if (menu.open && (node === undefined || !menu.contains(node))) {
      menu.open = false;
    }
  }
}

/**
 * Closes the innermost open menu, and puts focus back on the summary that
 * opened it.
 *
 * Innermost rather than all of them, because on a narrow screen the tools sit
 * inside the navigation menu and Escape should back out one step at a time
 * rather than clear the bar. `querySelectorAll` is in document order, so the
 * last open one is the deepest.
 *
 * Focus has to be put back deliberately. It is on something inside the menu
 * that is closing, and a `<details>` that closes under the focused element
 * drops focus to the body, which loses a keyboard reader their place on the
 * page entirely.
 */
export function closeInnermostMenu(menus: readonly HTMLDetailsElement[]): void {
  const open = menus.filter((menu) => menu.open);
  const innermost = open.at(-1);

  if (innermost === undefined) {
    return;
  }

  innermost.open = false;
  innermost.querySelector("summary")?.focus();
}
